import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Label,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Container,
  Table,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import PurchaseService from "../../../services/OrderingServices/PurchaseService";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import ItemService from "../../../services/OrderingServices/ItemService";
import SupplierService from "../../../services/OrderingServices/SupplierService";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";

// Validation Schema
const validationSchema = Yup.object({
  purchaseDate: Yup.date().required("Purchase date and time is required"),
  supplierId: Yup.number().required("Supplier is required"),
  items: Yup.array()
    .of(
      Yup.object({
        productId: Yup.number().required("Product is required"),
        productName: Yup.string().required("Product name is required"),
        quantity: Yup.number()
          .min(1, "Quantity must be at least 1")
          .required("Required"),
        unitPrice: Yup.number()
          .min(0, "Price cannot be negative")
          .required("Required"),
      })
    )
    .min(1, "At least one item is required"),
});

const PurchaseFormPage = () => {
  const { id } = useParams();
  const [orgDetail, setOrgDetail] = useState(null);
  const [productData, setProductData] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const flatpickrRef = useRef(null);
  const breadcrumbItems = [{ title: "Back To List", link: "/purchase" }];

  // Fetch purchase details
  useEffect(() => {
    const fetchOrgDetail = async () => {
      if (id) {
        try {
          setLoading(true);
          const response = await PurchaseService.detail(id);
          setOrgDetail(response);
        } catch (error) {
          console.error("Error fetching purchase details:", error);
          toast.error("Failed to load purchase details");
        } finally {
          setLoading(false);
        }
      }
    };
    fetchOrgDetail();
  }, [id]);

  // Fetch products
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await ItemService.itemList();
        setProductData(response);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products");
      }
    };
    fetchProduct();
  }, []);

  // Fetch suppliers
  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        const response = await SupplierService.getList(1, 100);
        setSupplierList(response);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        toast.error("Failed to load suppliers");
      }
    };
    fetchSupplier();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      purchaseDate: orgDetail?.purchaseDate || "",
      supplierId: orgDetail?.supplierId || "",
      items: orgDetail?.items || [
        {
          productId: "",
          productName: "",
          quantity: "",
          unitPrice: "",
        },
      ],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        const payload = {
          id: id ? parseInt(id) : undefined,
          purchaseDate: new Date(values.purchaseDate).toISOString(),
          supplierId: parseInt(values.supplierId),
          items: values.items.map((item) => ({
            productId: parseInt(item.productId),
            productName: item.productName,
            quantity: parseInt(item.quantity),
            unitPrice: parseFloat(item.unitPrice),
          })),
        };

        if (id) {
          await PurchaseService.update(id, payload);
          toast.success("Purchase updated successfully");
        } else {
          await PurchaseService.create(payload);
          toast.success("Purchase created successfully");
          resetForm();
        }
        navigate("/purchase");
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error(
          error?.response?.data?.message || "Failed to save purchase"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const productOptions =
    productData?.items?.map((type) => ({
      value: type.id,
      label: type.name,
      price: type.price,
    })) || [];

  const supplierOptions =
    supplierList?.map((supplier) => ({
      value: supplier.id,
      label: supplier.name,
    })) || [];

  const handleAddItem = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      {
        productId: "",
        productName: "",
        quantity: "",
        unitPrice: "",
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = formik.values.items.filter((_, i) => i !== index);
    formik.setFieldValue("items", newItems);
  };

  const handleProductChange = (index, selectedOption) => {
    formik.setFieldValue(
      `items[${index}].productId`,
      selectedOption ? selectedOption.value : ""
    );
    formik.setFieldValue(
      `items[${index}].productName`,
      selectedOption ? selectedOption.label : ""
    );
    formik.setFieldValue(
      `items[${index}].unitPrice`,
      selectedOption ? selectedOption.price || "" : ""
    );
  };

  const handleCancel = () => {
    navigate("/purchase");
  };

  const calculateTotal = (quantity, unitPrice) => {
    const qty = parseFloat(quantity) || 0;
    const price = parseFloat(unitPrice) || 0;
    return qty * price;
  };

  const calculateGrandTotal = () => {
    return formik.values.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      return sum + qty * price;
    }, 0);
  };

  if (loading && id) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          pageTitle="Purchase"
          title={id ? "Update Purchase" : "Add Purchase"}
          breadcrumbItems={breadcrumbItems}
        />
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            {/* Basic Information Card */}
            <Col lg={12}>
              <Card className="mb-3">
                <CardHeader>
                  <h5 className="card-title mb-0">Basic Information</h5>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={4}>
                      <div className="mb-3">
                        <Label>
                          Purchase Date{" "}
                          <span className="text-danger">*</span>
                        </Label>

                        <Flatpickr
                          options={{
                            // mode: "range",
                            dateFormat: "Y-m-d",
                          }}
                          value={formik.values.purchaseDate}
                          onChange={([date]) =>
                            formik.setFieldValue("purchaseDate", date)
                          }
                          placeholder="Select Date"
                          className="form-control"
                          onBlur={() =>
                            formik.setFieldTouched("purchaseDate", true)
                          }
                        />

                        {formik.touched.purchaseDate &&
                          formik.errors.purchaseDate && (
                            <div className="invalid-feedback d-block">
                              {formik.errors.purchaseDate}
                            </div>
                          )}
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <Label>
                          Supplier <span className="text-danger">*</span>
                        </Label>
                        <Select
                          options={supplierOptions}
                          value={
                            supplierOptions.find(
                              (option) =>
                                option.value === formik.values.supplierId
                            ) || null
                          }
                          onChange={(option) =>
                            formik.setFieldValue(
                              "supplierId",
                              option ? option.value : ""
                            )
                          }
                          placeholder="Select Supplier"
                          isClearable
                          className={
                            formik.touched.supplierId &&
                            formik.errors.supplierId
                              ? "is-invalid"
                              : ""
                          }
                        />
                        {formik.touched.supplierId &&
                          formik.errors.supplierId && (
                            <div className="invalid-feedback d-block">
                              {formik.errors.supplierId}
                            </div>
                          )}
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <Label>
                          Invoice No. <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          placeholder="Invoice No."
                          // value={filterText}
                          // onChange={(e) => setFilterText(e.target.value)}
                        />
                        {formik.touched.supplierId &&
                          formik.errors.supplierId && (
                            <div className="invalid-feedback d-block">
                              {formik.errors.supplierId}
                            </div>
                          )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Items DataTable */}
            <Col lg={12}>
              <Card className="mb-3">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">
                    Purchase Items Count({formik.values.items.length})
                  </h5>
                  <Button
                    color="success"
                    size="sm"
                    type="button"
                    onClick={handleAddItem}
                    className="btn-label"
                  >
                    <i className="bx bx-plus label-icon align-middle"></i>
                    Add
                  </Button>
                </CardHeader>
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table table-bordered table-hover align-middle mb-0">
                      <thead className="">
                        <tr>
                          <th style={{ width: "50px" }}>S.N. </th>
                          <th style={{ width: "35%" }}>
                            Product <span className="text-danger">*</span>
                          </th>
                          <th style={{ width: "15%" }}>
                            Quantity <span className="text-danger">*</span>
                          </th>
                          <th style={{ width: "15%" }}>
                            Unit Price <span className="text-danger">*</span>
                          </th>
                          <th style={{ width: "15%" }}>Total</th>
                          <th style={{ width: "80px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formik.values.items.length === 0 ? (
                          <tr>
                            <td
                              colSpan="6"
                              className="text-center text-muted py-4"
                            >
                              <i className="bx bx-package bx-lg d-block mb-2"></i>
                              <p className="mb-0">
                                No items added yet. Click "Add Row" to get
                                started.
                              </p>
                            </td>
                          </tr>
                        ) : (
                          formik.values.items.map((item, index) => (
                            <tr key={index}>
                              <td className="text-center">{index + 1}</td>
                              <td>
                                <Select
                                  options={productOptions}
                                  value={
                                    productOptions.find(
                                      (option) =>
                                        option.value === item.productId
                                    ) || null
                                  }
                                  onChange={(option) =>
                                    handleProductChange(index, option)
                                  }
                                  placeholder="Select Product"
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                    control: (base, state) => ({
                                      ...base,
                                      borderColor:
                                        formik.touched.items?.[index]
                                          ?.productId &&
                                        formik.errors.items?.[index]?.productId
                                          ? "#f46a6a"
                                          : base.borderColor,
                                    }),
                                  }}
                                />
                                {formik.touched.items?.[index]?.productId &&
                                  formik.errors.items?.[index]?.productId && (
                                    <div className="text-danger small mt-1">
                                      {formik.errors.items[index].productId}
                                    </div>
                                  )}
                              </td>
                              <td>
                                <Input
                                  name={`items[${index}].quantity`}
                                  type="number"
                                  value={item.quantity}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  placeholder="Qty"
                                  invalid={
                                    !!(
                                      formik.touched.items?.[index]?.quantity &&
                                      formik.errors.items?.[index]?.quantity
                                    )
                                  }
                                />
                                {formik.touched.items?.[index]?.quantity &&
                                  formik.errors.items?.[index]?.quantity && (
                                    <div className="text-danger small mt-1">
                                      {formik.errors.items[index].quantity}
                                    </div>
                                  )}
                              </td>
                              <td>
                                <Input
                                  name={`items[${index}].unitPrice`}
                                  type="number"
                                  step="0.01"
                                  value={item.unitPrice}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  placeholder="Price"
                                  invalid={
                                    !!(
                                      formik.touched.items?.[index]
                                        ?.unitPrice &&
                                      formik.errors.items?.[index]?.unitPrice
                                    )
                                  }
                                />
                                {formik.touched.items?.[index]?.unitPrice &&
                                  formik.errors.items?.[index]?.unitPrice && (
                                    <div className="text-danger small mt-1">
                                      {formik.errors.items[index].unitPrice}
                                    </div>
                                  )}
                              </td>
                              <td className="text-end fw-semibold">
                                Rs.{" "}
                                <NumberWithCommas
                                  number={calculateTotal(
                                    item.quantity,
                                    item.unitPrice
                                  )}
                                />
                              </td>

                              <td className="text-center">
                                <Button
                                  color="danger"
                                  size="sm"
                                  type="button"
                                  onClick={() => handleRemoveItem(index)}
                                  className="btn-icon"
                                  disabled={formik.values.items.length === 1}
                                >
                                  <i className="bx bx-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      {formik.values.items.length > 0 && (
                        <tfoot className="">
                          <tr>
                            <td colSpan="4" className="text-end fw-bold">
                              Grand Total:
                            </td>
                            <td className="text-end fw-bold fs-5 text-primary">
                              Rs.{" "}
                              <NumberWithCommas
                                number={calculateGrandTotal()}
                              />
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      )}
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Sticky Action Buttons */}
          <div className="position-sticky bottom-0 p-3 mt-3">
            <div className="d-flex justify-content-end gap-2">
              <Button color="light" type="button" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                color="primary"
                type="button"
                onClick={formik.handleSubmit}
                disabled={loading}
              >
                {loading && <Spinner size="sm" className="me-2" />}
                {id ? "Update Purchase" : "Create Purchase"}
              </Button>
            </div>
          </div>
        </Form>
      </Container>
    </div>
  );
};

export default PurchaseFormPage;
